import {
    Button,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Chip,
    Dialog,
    Menu,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
    ProcurementProcessWithRelations,
    useGetProcurementProcessesQuery,
} from '../../../redux/vmsApi';
import { useState } from 'react';
import React from 'react';
import RequisitionsFilter from '../common/RequisitionsFilter';
import config from '../../../config';
import { useRouter } from '../../../hooks/useRouter';

const RequisitionsList: React.FC = () => {
    const router = useRouter();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [filters, setFilters] = useState({
        title: '',
        approved: undefined,
        development: '',
        reqNumber: '',
        fromDate: '2024-10-01',
        toDate: '2025-12-20',
        currentTask: 'all',
    });

    const [openDialog, setOpenDialog] = useState<string | null>(null);
    const handleDialogOpen = (path: string) => setOpenDialog(`${config.CDN_URL}/procurement/${path}`);
    const handleDialogClose = () => setOpenDialog(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedProcess, setSelectedProcess] = useState<ProcurementProcessWithRelations | null>(null);

    const handleOpen = (
      event: React.MouseEvent<HTMLButtonElement>,
      process: ProcurementProcessWithRelations
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedProcess(process);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedProcess(null);
    };

    const handleViewProcessDetails = (procurementProcessId: string | undefined) => {
        window.open(`/procurement/procurementProcess/${procurementProcessId}`, '_blank');
    };

    const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const { data: procurementProcesses, isLoading, isError } = useGetProcurementProcessesQuery(apiParams);

    const handleFilterChange = (field: string, value: string) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: value,
        }));
    };

    const handleApplyFilters = () => {
        setApiParams({
            ...filters,
            skip: 0,
            limit: pageSize,
        });
        setPage(0);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setApiParams((prevParams) => ({
            ...prevParams,
            skip: newPage * pageSize,
        }));
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        const newSize = Number(event.target.value);
        setPageSize(newSize);
        setPage(0);
        setApiParams((prevParams) => ({
            ...prevParams,
            limit: newSize,
            skip: 0,
        }));
    };

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <div>Error fetching Procurement Processes.</div>;

    return (
      <React.Fragment>
          <RequisitionsFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilters={handleApplyFilters}
          />

          <TableContainer
            component={Paper}
            sx={{
                boxShadow: 3,
                borderRadius: 2,
                overflowX: 'auto',
            }}
          >
              <Table size={isMobile ? 'small' : 'medium'} sx={{ minWidth: 880 }}>
                  <TableHead sx={{ backgroundColor: '#1976d2' }}>
                      <TableRow>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Number</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Title</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Raised By</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Date Raised</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Development</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Approved</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Actions</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {procurementProcesses && procurementProcesses.length > 0 ? (
                        procurementProcesses.map((procurementProcess, index) => (
                          <TableRow key={procurementProcess.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{procurementProcess.requisition?.reqNumber || 'N/A'}</TableCell>
                              <TableCell sx={{ minWidth: 180 }}>{procurementProcess.requisition?.title || 'N/A'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{procurementProcess.initiatedByUser?.name || 'N/A'}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {procurementProcess.initiatedDate
                                    ? new Date(procurementProcess.initiatedDate).toLocaleDateString()
                                    : 'N/A'}
                              </TableCell>
                              <TableCell sx={{ minWidth: 220 }}>
                                  {procurementProcess.requisition?.development?.name || 'N/A'},{' '}
                                  {procurementProcess.requisition?.project?.name || 'N/A'}
                              </TableCell>
                              <TableCell>
                                  {procurementProcess.requisition?.approved ? (
                                    <Chip label="Approved" color="success" />
                                  ) : (
                                    <Chip label="Not Yet Approved" color="error" />
                                  )}
                              </TableCell>
                              <TableCell>
                                  <Button
                                    variant={'contained'}
                                                                        size={isMobile ? 'small' : 'medium'}
                                    onClick={(e) => handleOpen(e, procurementProcess)}
                                  >
                                      Actions
                                  </Button>
                              </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                            <TableCell colSpan={9} align="center">
                                No matching Procurement Processes found.
                            </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
              </Table>
          </TableContainer>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            slotProps={{
                paper: {
                    elevation: 3,
                    sx: {
                        minWidth: 200,
                        borderRadius: 1,
                    },
                },
            }}
          >
              <MenuItem
                onClick={() => {
                    handleClose();
                    if (selectedProcess?.requisitionDocument?.name) {
                        handleDialogOpen(selectedProcess.requisitionDocument.name);
                    }
                }}
              >
                  View Document
              </MenuItem>
              <MenuItem
                onClick={() => {
                    handleClose();
                    if (selectedProcess?.id) {
                        handleViewProcessDetails(selectedProcess.id);
                    }
                }}
              >
                  View Process Details
              </MenuItem>
          </Menu>

                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        mt={2}
                        gap={1}
                        flexWrap="wrap"
                    >
              <Button
                onClick={() => handlePageChange(Math.max(page - 1, 0))}
                disabled={page === 0}
                variant="contained"
                                sx={{ minWidth: 100 }}
              >
                  Previous
              </Button>

              <Typography variant="body1">Page {page + 1}</Typography>

              <Button
                onClick={() => handlePageChange(page + 1)}
                disabled={procurementProcesses?.length === undefined || procurementProcesses.length < pageSize}
                variant="contained"
                sx={{ minWidth: 100 }}
              >
                  Next
              </Button>
          </Box>

          <Box mt={2} display="flex" justifyContent="center" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" sx={{ marginRight: 2 }}>
                  Rows per page:
              </Typography>
              <FormControl variant="outlined" size="small">
                  <Select value={pageSize} onChange={handlePageSizeChange} sx={{ backgroundColor: '#f0f4f7' }}>
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                  </Select>
              </FormControl>
          </Box>

          <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
              <Box p={2}>
                  {openDialog && (
                    <iframe
                      src={openDialog}
                      title="Document Viewer"
                      width="100%"
                      height="500px"
                      style={{ border: 'none' }}
                    />
                  )}
                  <Box textAlign="right" mt={2}>
                      <Button onClick={handleDialogClose} variant="contained" color="primary">
                          Close
                      </Button>
                  </Box>
              </Box>
          </Dialog>
      </React.Fragment>
    );
};

export default RequisitionsList;
